// One conduct record's description. The board now saves the box text ("Em a, b và c."); older records are
// ticked presets joined with "; ", then " - " and the note, which parseDescription still splits for the card.
import { REASON_PRESETS } from '../../config/violation-reasons.ts';
import { EMPTY_CHECKLIST, type ChecklistText, type SentenceRule } from './checklistText.ts';

const REASON_SEPARATOR = '; ';
const NOTE_SEPARATOR = ' - ';
const ALL_PRESETS: readonly string[] = [...REASON_PRESETS.commendation, ...REASON_PRESETS.violation];

export type ConductKind = keyof typeof REASON_PRESETS;

export const CONDUCT_RULES: Record<ConductKind, SentenceRule> = {
  commendation: { subject: 'Em', order: REASON_PRESETS.commendation },
  violation: { subject: 'Em', order: REASON_PRESETS.violation },
};

export interface ConductEntryLines {
  reasons: string[];
  note: string;
}

/** The box text as saved: lms-api's daily contact-book post appends " trong tiết <môn>." itself, so a final period would print ". trong tiết". */
export function conductDescription(text: string): string {
  return text.trim().replace(/[.\s]+$/, '');
}

// reward and discipline are drafted apart: each type keeps its own ticks and text
export type ConductDrafts = Record<ConductKind, ChecklistText>;

export const EMPTY_DRAFTS: ConductDrafts = { commendation: EMPTY_CHECKLIST, violation: EMPTY_CHECKLIST };

const KINDS: ConductKind[] = ['commendation', 'violation'];

/** One record per filled draft, reward first; a record has one type, so the two are never mixed into one text. */
export function draftEntries(drafts: ConductDrafts): Array<{ type: ConductKind; description: string }> {
  return KINDS.map((type) => ({ type, description: conductDescription(drafts[type].text) })).filter((entry) => entry.description !== '');
}

/** Drafts left after a save: the saved types are emptied so a retry never records them twice. */
export function clearSaved(drafts: ConductDrafts, saved: readonly ConductKind[]): ConductDrafts {
  return { commendation: saved.indexOf('commendation') >= 0 ? EMPTY_CHECKLIST : drafts.commendation, violation: saved.indexOf('violation') >= 0 ? EMPTY_CHECKLIST : drafts.violation };
}

function leadingPreset(rest: string): string | null {
  for (const preset of ALL_PRESETS) {
    if (rest.indexOf(preset) !== 0) continue;
    const tail = rest.slice(preset.length);
    if (tail === '' || tail.indexOf(REASON_SEPARATOR) === 0 || tail.indexOf(NOTE_SEPARATOR) === 0) return preset;
  }
  return null;
}

/** Splits a stored description back into its reasons and note; free text from QLT web splits on "; " only. */
export function parseDescription(description: string): ConductEntryLines {
  const text = (description || '').trim();
  const reasons: string[] = [];
  let rest = text;
  for (let preset = leadingPreset(rest); preset !== null; preset = leadingPreset(rest)) {
    reasons.push(preset);
    rest = rest.slice(preset.length);
    if (rest.indexOf(REASON_SEPARATOR) !== 0) break;
    rest = rest.slice(REASON_SEPARATOR.length);
  }
  if (reasons.length === 0) {
    return { reasons: text.split(REASON_SEPARATOR).map((part) => part.trim()).filter(Boolean), note: '' };
  }
  if (rest.indexOf(NOTE_SEPARATOR) === 0) return { reasons, note: rest.slice(NOTE_SEPARATOR.length).trim() };
  // a preset run cut short by free text: keep that text as its own line rather than drop it
  return rest.trim() ? { reasons: reasons.concat(rest.trim()), note: '' } : { reasons, note: '' };
}
